import { Icon } from "@iconify/react";
import Dialog from "@mui/material/Dialog";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../components/shared/Card";
import { FileDropzone } from "../../components/shared/FileDropzone";
import { Instructions } from "../../components/shared/Instructions";
import { CheckBoxWithLabel } from "../../components/shared/InputField";
import { SummaryMetricCard } from "../../components/shared/SummaryMetricCard";
import {
  ButtonSize,
  HStack,
  PrimaryCTAButton,
  SecondaryCTAButton,
  TertiaryCTAButton,
  VStack,
} from "../../components/utils";
import { getEmployeeHistoryImportTemplate } from "../../api/Esop";
import {
  useCommitEmployeeHistoryImport,
  useValidateEmployeeHistoryImport,
} from "../../queries/employees";
import BCHeader from "../../shared/BCHeader";
import { EmployeeHistoryImportReview } from "../../types/Employee";
import convertToBase64 from "../../utils/convertToBase64";
import { downloadBlobObject } from "../../utils/DownloadFile";

function displayDate(value?: string) {
  if (!value) return "-";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

function changedFields(review: EmployeeHistoryImportReview["rows"][number]) {
  return review.changes
    .map((change) => change.fieldLabel || change.fieldName)
    .join(", ");
}

export default function EmployeeHistoryImportPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState<File>();
  const [review, setReview] = useState<EmployeeHistoryImportReview>();
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const validateImport = useValidateEmployeeHistoryImport();
  const commitImport = useCommitEmployeeHistoryImport();
  const validRows = useMemo(
    () => review?.rows.filter((row) => row.status === "VALID") || [],
    [review]
  );

  const validate = async () => {
    if (!file) return;
    try {
      const response = await validateImport.mutateAsync(
        (await convertToBase64(file)) as string
      );
      setReview(response);
      setSelectedRowIds(
        response.rows
          .filter((row) => row.status === "VALID")
          .map((row) => row.rowId)
      );
    } catch (error: any) {
      toast.error(
        error?.response?.data?.errorRaw ||
          error?.response?.data?.reason ||
          "We couldn't validate this history import"
      );
    }
  };

  const commit = async () => {
    if (!review) return;
    try {
      const result = await commitImport.mutateAsync({
        cacheId: review.cacheId,
        rowIds: selectedRowIds,
      });
      setConfirmOpen(false);
      toast.success(
        `${result.importedRows} employee history event${
          result.importedRows === 1 ? "" : "s"
        } imported`
      );
      navigate("/options/allEmployees");
    } catch (error: any) {
      setConfirmOpen(false);
      toast.error(
        error?.response?.data?.errorRaw ||
          error?.response?.data?.reason ||
          "We couldn't import the selected history events"
      );
    }
  };

  const toggleRow = (rowId: string, checked: boolean) => {
    setSelectedRowIds((current) =>
      checked
        ? [...new Set([...current, rowId])]
        : current.filter((id) => id !== rowId)
    );
  };

  if (review) {
    return (
      <main className="w-full">
        <VStack className="w-full gap-6">
          <BCHeader
            className="items-baseline"
            bcTitle="Stakeholders"
            bcSubTitle="Import employee history"
            onClick={() => navigate("/options/allEmployees")}
          />
          <HStack className="w-full flex-col items-start justify-between gap-4 sm:flex-row">
            <VStack className="min-w-0 gap-1">
              <h1 className="text-xl font-semibold leading-7 text-headerColor">
                Review employee history
              </h1>
              <p className="text-sm leading-5 text-secondaryText">
                Only selected valid rows will be added to Activity Log. Employee
                profiles and grants are not changed.
              </p>
            </VStack>
            <TertiaryCTAButton
              onClick={() => {
                setReview(undefined);
                setSelectedRowIds([]);
              }}
            >
              Upload another file
            </TertiaryCTAButton>
          </HStack>
          <section
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5"
            aria-label="Import summary"
          >
            <SummaryMetricCard
              label="Total rows"
              value={review.summary.totalRows}
            />
            <SummaryMetricCard label="Ready" value={review.summary.validRows} />
            <SummaryMetricCard
              label="Needs correction"
              value={review.summary.errorRows}
            />
            <SummaryMetricCard
              label="Internal transfers"
              value={review.summary.internalTransfers}
            />
            <SummaryMetricCard
              label="Profile updates"
              value={review.summary.profileUpdates}
            />
          </section>
          <Card className="border-borderColor bg-white shadow-card">
            <CardHeader className="border-b border-borderColor p-4 sm:p-6">
              <HStack className="w-full flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
                <CardTitle>History events</CardTitle>
                <CheckBoxWithLabel
                  id="select-all-history"
                  label={`Select all ready rows (${validRows.length})`}
                  labelclassname="ml-2 text-sm text-primaryText"
                  className="h-4 w-4 accent-primary"
                  checked={
                    validRows.length > 0 &&
                    selectedRowIds.length === validRows.length
                  }
                  onChange={(event) =>
                    setSelectedRowIds(
                      event.target.checked
                        ? validRows.map((row) => row.rowId)
                        : []
                    )
                  }
                />
              </HStack>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-borderColor text-left text-sm">
                  <thead className="bg-tertiaryBg text-xs font-medium text-secondaryText">
                    <tr>
                      <th className="px-4 py-3">Import</th>
                      <th className="px-4 py-3">Employee</th>
                      <th className="px-4 py-3">Effective date</th>
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3">Changed fields</th>
                      <th className="px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-borderColor text-primaryText">
                    {review.rows.map((row) => (
                      <tr key={row.rowId}>
                        <td className="px-4 py-3">
                          <CheckBoxWithLabel
                            id={`history-row-${row.rowId}`}
                            label={`Import row ${row.rowNumber}`}
                            labelclassname="sr-only"
                            className="h-4 w-4 accent-primary"
                            checked={selectedRowIds.includes(row.rowId)}
                            disabled={row.status !== "VALID"}
                            onChange={(event) =>
                              toggleRow(row.rowId, event.target.checked)
                            }
                          />
                        </td>
                        <td className="px-4 py-3">
                          <VStack className="gap-1">
                            <span className="font-medium">
                              {row.employeeName || row.currentEmployeeId}
                            </span>
                            <span className="text-xs text-secondaryText">
                              {row.currentEmployeeId}
                            </span>
                          </VStack>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {displayDate(row.effectiveDate)}
                        </td>
                        <td className="px-4 py-3">
                          <span className="rounded-lg bg-tertiaryBg px-2 py-1 text-xs font-medium">
                            {row.changeType === "INTERNAL_TRANSFER"
                              ? "Internal transfer"
                              : row.changeType === "PROFILE_UPDATE"
                              ? "Profile update"
                              : "-"}
                          </span>
                        </td>
                        <td className="max-w-md px-4 py-3 text-xs2">
                          {changedFields(row) || "-"}
                        </td>
                        <td className="px-4 py-3">
                          <VStack className="gap-1">
                            <span
                              className={
                                row.status === "VALID"
                                  ? "text-xs font-medium text-green-700"
                                  : "text-xs font-medium text-red-700"
                              }
                            >
                              {row.status === "VALID"
                                ? "Ready"
                                : "Needs correction"}
                            </span>
                            {row.errors.map((error) => (
                              <span
                                key={error}
                                className="max-w-xs text-xs text-red-700"
                              >
                                {error}
                              </span>
                            ))}
                          </VStack>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          <HStack className="w-full flex-col justify-end gap-3 sm:flex-row">
            <TertiaryCTAButton
              onClick={() => navigate("/options/allEmployees")}
            >
              Cancel
            </TertiaryCTAButton>
            <PrimaryCTAButton
              buttonSize={ButtonSize.MEDIUM}
              disabled={!selectedRowIds.length || commitImport.isLoading}
              onClick={() => setConfirmOpen(true)}
            >
              <Icon
                icon="material-symbols:history-rounded"
                height={18}
                className="mr-2"
              />
              {commitImport.isLoading
                ? "Importing history"
                : `Import ${selectedRowIds.length} selected`}
            </PrimaryCTAButton>
          </HStack>
        </VStack>
        <Dialog
          open={confirmOpen}
          onClose={() => !commitImport.isLoading && setConfirmOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <VStack className="bg-white">
            <div className="p-6">
              <h2 className="text-base font-semibold leading-6 text-headerColor">
                Import employee history
              </h2>
              <p className="mt-2 text-sm leading-5 text-secondaryText">
                This will add {selectedRowIds.length} history event
                {selectedRowIds.length === 1 ? "" : "s"} to Activity Log. These
                entries cannot be edited from this workflow.
              </p>
            </div>
            <HStack className="justify-end gap-3 border-t border-borderColor px-6 py-4">
              <SecondaryCTAButton
                disabled={commitImport.isLoading}
                onClick={() => setConfirmOpen(false)}
              >
                Cancel
              </SecondaryCTAButton>
              <PrimaryCTAButton
                disabled={commitImport.isLoading}
                onClick={commit}
              >
                {commitImport.isLoading
                  ? "Importing history"
                  : "Import history"}
              </PrimaryCTAButton>
            </HStack>
          </VStack>
        </Dialog>
      </main>
    );
  }

  return (
    <main className="w-full">
      <VStack className="w-full gap-6">
        <BCHeader
          className="items-baseline"
          bcTitle="Stakeholders"
          bcSubTitle="Import employee history"
          onClick={() => navigate("/options/allEmployees")}
        />
        <HStack className="w-full flex-col items-start justify-between gap-4 sm:flex-row">
          <VStack className="min-w-0 gap-1">
            <h1 className="text-xl font-semibold leading-7 text-headerColor">
              Import employee history
            </h1>
            <p className="text-sm leading-5 text-secondaryText">
              Add historical profile updates and internal transfers to Activity
              Log without changing live employee data.
            </p>
          </VStack>
          <PrimaryCTAButton
            buttonSize={ButtonSize.MEDIUM}
            loading={downloadingTemplate}
            onClick={async () => {
              setDownloadingTemplate(true);
              try {
                downloadBlobObject(
                  await getEmployeeHistoryImportTemplate(),
                  "Employee_History_Import_Template.xlsx"
                );
              } catch {
                toast.error("We couldn't download the history import template");
              } finally {
                setDownloadingTemplate(false);
              }
            }}
          >
            <Icon icon="mdi:download-outline" width={18} className="mr-2" />
            Download template
          </PrimaryCTAButton>
        </HStack>
        <Card className="flex min-h-[600px] flex-col border-borderColor">
          <CardContent className="flex min-h-[440px] flex-1 flex-col !px-8 !py-6">
            <VStack className="m-auto w-full max-w-2xl gap-6">
              <VStack className="gap-1 text-center">
                <h2 className="text-xl font-medium text-primaryText">
                  Upload file
                </h2>
                <p className="text-sm text-secondaryText">
                  Please ensure your file follows the template format before
                  uploading.
                </p>
              </VStack>
              <VStack className="gap-3">
                <FileDropzone
                  selectedFile={file}
                  onFileSelect={setFile}
                  onClear={() => setFile(undefined)}
                  disabled={validateImport.isLoading}
                  acceptedFileExtensions={[".xlsx"]}
                  instructionText="Excel files only (.xlsx)"
                />
                <p className="text-center text-xs italic text-secondaryText">
                  Important: Use the downloaded template and do not leave empty
                  rows before the header row.
                </p>
              </VStack>
              <Instructions
                data={[
                  "Use Current Employee ID to find the existing employee record",
                  "Use YYYY-MM-DD for Effective Date",
                  "Enter previous and new values for every changed field",
                  "Use <CLEAR> as the new value when clearing a nullable field",
                  "This import records history only. It does not change employees, grants, access, or resignations.",
                ]}
              />
            </VStack>
          </CardContent>
          {file && (
            <CardFooter className="w-full justify-end gap-3 border-t border-borderColor !p-4">
              <PrimaryCTAButton
                buttonSize={ButtonSize.MEDIUM}
                disabled={validateImport.isLoading}
                loading={validateImport.isLoading}
                loadingText="Validating workbook"
                onClick={validate}
              >
                <Icon
                  icon="material-symbols:preview-rounded"
                  height={18}
                  className="mr-2"
                />
                Preview history
              </PrimaryCTAButton>
            </CardFooter>
          )}
        </Card>
      </VStack>
    </main>
  );
}
